"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HomeHero } from "../../../sanity/lib/types/homeHero";
import { VideoElement } from "../Video";
import { ImageElement } from "../Image";
import useIsMobile from "../../hooks/useIsMobile";
import SplitText from "gsap/SplitText";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

interface HomeHeroProps {
    topics: string[];
    claim: string;
    asset: HomeHero["asset"];
}

export const HeroHomeClient = ({ topics, claim, asset }: HomeHeroProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const spanRef = useRef<HTMLSpanElement>(null);
    const { isMobile } = useIsMobile();

    useGSAP(
        () => {
            let mm = gsap.matchMedia();

            mm.add("(prefers-reduced-motion: reduce)", (context) => {
                gsap.set(".headline", { opacity: 1 });
                gsap.set(".subline", { opacity: 1 });
            });

            mm.add("(prefers-reduced-motion: no-preference)", () => {
                gsap.set(".js-nav-logo", { opacity: 0 });

                const heroText = SplitText.create(".home .headline", {
                    type: "lines, words",
                    mask: "lines",
                });

                const sublineText = document.querySelectorAll(".subline");

                const heroImage = containerRef.current?.querySelector(
                    ".placeholder-image",
                ) as HTMLImageElement;

                gsap.set(heroText.words, { y: "110%" });

                gsap.set(".headline", { opacity: 1 });
                gsap.set(".subline", { opacity: 1 });

                gsap.set(heroImage, { opacity: 0 });

                gsap.to(heroText.words, {
                    duration: 1,
                    y: 0,
                    stagger: 0.1,
                    ease: "power4.out",
                    delay: 1.5,
                    mask: "lines",
                });

                gsap.to([heroText.words[0], heroText.words[2]], {
                    duration: 1,
                    y: "-110%",
                    stagger: 0.1,
                    ease: "power4.out",
                    delay: 3.0,
                    mask: "lines",
                });

                gsap.to(sublineText, {
                    duration: 4,
                    y: "-100%",
                    ease: "power4.out",
                    delay: 3.5,
                });

                gsap.to(".js-nav-logo", { opacity: 1, delay: 2 });

                gsap.to(heroImage, {
                    opacity: 1,
                    ease: "power4.out",
                    duration: 1,
                    delay: 2.5,
                });

                gsap.timeline();
            });
        },
        { dependencies: [] },
    );

    useGSAP(
        () => {
            let mm = gsap.matchMedia();
            let tl: gsap.core.Timeline;

            mm.add("(prefers-reduced-motion: no-preference)", () => {
                const heroImage = containerRef.current?.querySelector(
                    ".placeholder-image",
                ) as HTMLImageElement;

                if (spanRef.current) {
                    tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: ".hero-image",
                            start: "bottom bottom",
                            end: "bottom 10%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });

                    tl.to(heroImage, {
                        width: "170px",
                        y: spanRef.current.offsetTop - 96.65198516845703,
                        x: spanRef.current.offsetLeft,
                        ease: "none",
                    });
                }

                ScrollTrigger.refresh();
            });

            return () => {
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
                tl.kill();
            };
        },
        { dependencies: [] },
    );

    return (
        <div className="container overflow-hidden home intersection-hero" ref={containerRef}>
            <div className="relative mx-2.5 md:mx-5 ">
                <p className="flex flex-col mt-8 mb-16 opacity-0 headline m-display-1 md:d-display-home whitespace-break-spaces">
                    <span>dots</span>
                    <span>and</span>
                    <span>lines</span>
                </p>

                <div className="absolute -bottom-2.5 left-0 w-full overflow-hidden m-display-1 md:d-display-home font-headline whitespace-break-spaces">
                    <div className="opacity-0">{topics[0]}</div>
                    <div className="absolute subline">
                        {topics?.map((word, index) => (
                            <span key={index} className="relative block pt-4">
                                {word}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            <div className="relative mx-2.5 md:mx-5">
                <div className="hero-image block w-full aspect-[1392/786]">
                    {isMobile &&
                        (asset?.asset?.[0]._type === "image" ? (
                            <ImageElement
                                width={2560}
                                src={asset.asset[0]}
                                alt={asset.alt || ""}
                                className="aspect-[1392/786]"
                            />
                        ) : (
                            <VideoElement
                                id="hero-video-home"
                                src={asset?.asset?.[0].assetUrl?.url || ""}
                                className="aspect-[1392/786]"
                            />
                        ))}
                </div>

                <div className="my-20 m-display-2 lg:text-claim">
                    {claim?.split("[]").map((part, index) => {
                        if (index === 1) {
                            return (
                                <span key={index}>
                                    <span
                                        key="spanRef"
                                        ref={spanRef}
                                        className="hidden md:inline-block md:w-[170px] mx-1 spanRef"
                                    >
                                        {!isMobile &&
                                            (asset?.asset?.[0]._type === "image" ? (
                                                <ImageElement
                                                    width={2560}
                                                    src={asset.asset[0]}
                                                    alt={asset.alt || ""}
                                                    className="absolute top-0 left-0 w-full h-auto placeholder-image aspect-[1392/786]"
                                                />
                                            ) : (
                                                <VideoElement
                                                    id="hero-video-home"
                                                    src={asset?.asset?.[0].assetUrl?.url || ""}
                                                    className="absolute top-0 left-0 w-full h-auto placeholder-image aspect-[1392/786]"
                                                />
                                            ))}
                                    </span>
                                    <span>{part.trim()}</span>
                                </span>
                            );
                        } else {
                            return (
                                <span key={index}>
                                    {part.trim()}
                                    <span className="md:hidden"> </span>
                                </span>
                            );
                        }
                    })}
                </div>
            </div>
        </div>
    );
};
